import { QueryClient } from '@tanstack/react-query'

const DEFAULT_STALE_TIME = 30_000
const DEFAULT_GC_TIME = 5 * 60_000
const DEFAULT_MAX_RETRIES = 2

export type CreateProductQueryClientOptions = {
  staleTime?: number
  gcTime?: number
  maxRetries?: number
  shouldRetryError?: (error: unknown) => boolean
}

export function readErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' && Number.isFinite(status) ? status : undefined
}

export function isRetryableApiFailure(error: unknown): boolean {
  const status = readErrorStatus(error)
  if (status === undefined || status === 0) return true
  if (status >= 400 && status < 500) return false
  if (status === 502 || status === 503) return true
  if (status >= 500) return true
  return false
}

function defaultRetry(
  failureCount: number,
  error: unknown,
  maxRetries: number,
  shouldRetryError?: (error: unknown) => boolean,
): boolean {
  if (failureCount >= maxRetries) return false
  if (shouldRetryError && !shouldRetryError(error)) return false
  if (!isRetryableApiFailure(error)) return false
  return true
}

export function createProductQueryClient(
  options: CreateProductQueryClientOptions = {},
): QueryClient {
  const staleTime = options.staleTime ?? DEFAULT_STALE_TIME
  const gcTime = options.gcTime ?? DEFAULT_GC_TIME
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES
  const { shouldRetryError } = options

  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime,
        gcTime,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
          defaultRetry(failureCount, error, maxRetries, shouldRetryError),
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  })
}

export function createTestQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  })
}
